import type { ReactNode } from 'react'
import { CheckboxInput, RadioInput } from '@/components/atoms'

interface SelectableCardProps {
  label: string
  selected?: boolean
  disabled?: boolean
  type?: 'checkbox' | 'radio'
  icon?: ReactNode
  description?: string
  onClick?: () => void
  className?: string
}

export function SelectableCard({
  label,
  selected = false,
  disabled = false,
  type = 'checkbox',
  icon,
  description,
  onClick,
  className,
}: SelectableCardProps) {
  const bgColor = !disabled ? 'bg-white' : 'bg-neutral-50'
  const borderColor = disabled
    ? 'border-indigo-100'
    : selected
      ? 'border-indigo-base'
      : 'border-neutral-100'
  const textColor = disabled ? 'text-neutral-base' : 'text-neutral-900'
  const iconColor = disabled
    ? 'text-indigo-100'
    : selected
      ? 'text-indigo-base'
      : 'text-neutral-900'

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex cursor-pointer items-center gap-4 rounded-lg border p-4 transition',
        disabled ? 'cursor-not-allowed' : '',
        bgColor,
        borderColor,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {type === 'checkbox' ? (
        <CheckboxInput checked={selected} disabled={disabled} readOnly />
      ) : (
        <RadioInput checked={selected} disabled={disabled} readOnly />
      )}

      <span className={`flex items-center gap-2 ${textColor}`}>
        {icon && (
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center ${iconColor}`}
          >
            {icon}
          </span>
        )}

        {description ? (
          <span className="flex flex-col gap-1 text-left">
            <span className="tracking-base text-xs leading-4 font-bold">
              {label}
            </span>
            <span className="tracking-base text-xs leading-4">
              {description}
            </span>
          </span>
        ) : (
          <span className="tracking-base text-left text-xs leading-4">
            {label}
          </span>
        )}
      </span>
    </button>
  )
}
